import React, { useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardHeader, CardBody, Heading, useColorMode } from '@chakra-ui/react';
import { RotateCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getPurchasePaidReport } from '../../../../redux/actions/dashboard';

const PurchasePaidChart = ({ data, financialAnalysisDateRange, user }) => {
   const { colorMode } = useColorMode();
   const isDarkMode = colorMode === 'dark';
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   if (!data || data.length === 0) return null;

   const labels = data.map((item) => item.period_label);
   const paid = data.map((item) => parseFloat(item.total_paid_amount));
   const unpaid = data.map((item) => {
      const total = parseFloat(item.total_purchase_amount);
      const paid = parseFloat(item.total_paid_amount);
      return Math.max(0, total - paid); // Avoid negative unpaid
   });

   const options = {
      chart: {
         type: 'bar',
         stacked: true,
         background: 'transparent',
         toolbar: {
            tools: {
               download: true,
               selection: true,
               zoom: true,
               zoomin: true,
               zoomout: true,
               pan: true,
               reset: true,
            },
         },
      },
      xaxis: {
         categories: labels,
         labels: {
            style: {
               colors: isDarkMode ? 'white' : '#374151',
            },
         },
         axisBorder: {
            color: isDarkMode ? '#4A5568' : '#E2E8F0',
         },
         axisTicks: {
            color: isDarkMode ? '#4A5568' : '#E2E8F0',
         },
      },
      yaxis: {
         labels: {
            formatter: (val) => `₹${val.toLocaleString('en-IN')}`,
            style: {
               colors: isDarkMode ? 'white' : '#374151',
            },
         },
         title: {
            text: 'Amount (INR)',
            style: {
               color: isDarkMode ? 'white' : '#374151',
            },
         },
      },
      tooltip: {
         theme: isDarkMode ? 'dark' : 'light',
         y: {
            formatter: (val) => `₹${val.toLocaleString('en-IN')}`,
         },
      },
      colors: [isDarkMode ? '#4299E1' : '#2B6CB0', isDarkMode ? '#FC8181' : '#E53E3E'], // Paid (blue), Unpaid (red)
      legend: {
         position: 'top',
         labels: {
            colors: isDarkMode ? 'white' : '#374151',
         },
      },
      dataLabels: {
         enabled: false,
      },
      grid: {
         borderColor: isDarkMode ? '#4A5568' : '#E2E8F0',
      },
      plotOptions: {
         bar: {
            borderRadius: 4,
            columnWidth: '70%',
         },
      },
   };

   const series = [
      {
         name: 'Paid',
         data: paid,
      },
      {
         name: 'Unpaid',
         data: unpaid,
      },
   ];

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getPurchasePaidReport({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <Card bg={isDarkMode ? 'gray.700' : 'white'}>
         <CardHeader display="flex">
            <Heading size="md" color={isDarkMode ? 'white' : 'gray.800'}>
               Purchase vs Paid — Monthly Trend
            </Heading>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${isDarkMode ? "hover:bg-gray-600 text-blue-400" : "hover:bg-gray-100 text-blue-600"
                  }`}
            >
               <RotateCw
                  className={`w-5 h-5 transform ${isRotating ? "rotate-[360deg]" : ""
                     } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? "700ms" : "10ms",
                  }}
               />
            </button>
         </CardHeader>
         <CardBody>
            <ReactApexChart options={options} series={series} type="bar" height={350} />
         </CardBody>
      </Card>
   );
};

export default PurchasePaidChart;
